// apps/web/src/pages/Admin/AnimatedCoinManager.jsx
// "Coin Media Type" selector + animated (Signature) coin upload, preview,
// processing status and retries. Lives inside the existing Coin Management
// section; the static image drop zones there are untouched.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import adminApi from '../../services/adminApi';
import styles from './AdminCollections.module.css';

const POLL_MS = 3000;
const VIDEO_TYPES = ['video/mp4', 'video/webm'];

const STATUS_LABEL = {
    UPLOADING:  '⬆️ Uploading',
    PROCESSING: '⏳ Processing',
    READY:      '✅ Ready',
    FAILED:     '❌ Failed',
};

function isInFlight(version) {
    return version.status === 'UPLOADING' || version.status === 'PROCESSING' || version.gifStatus === 'PENDING';
}

// ── Looping preview with play/pause, mute and loop controls ──────────────────
export function AnimationPreview({ src, poster, label }) {
    const videoRef = useRef(null);
    const [playing, setPlaying] = useState(true);
    const [muted, setMuted]     = useState(true);
    const [loop, setLoop]       = useState(true);

    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        el.muted = muted;
        el.loop = loop;
    }, [muted, loop]);

    function togglePlay() {
        const el = videoRef.current;
        if (!el) return;
        if (el.paused) { el.play?.()?.catch?.(() => {}); setPlaying(true); }
        else { el.pause(); setPlaying(false); }
    }

    return (
        <div className={styles.animPreview}>
            <span className={styles.labelSmall}>{label}</span>
            <video
                ref={videoRef}
                className={styles.animPreviewVideo}
                src={src}
                poster={poster || undefined}
                autoPlay
                muted
                loop
                playsInline
                data-testid="animation-preview"
                onEnded={() => setPlaying(false)}
            />
            <div className={styles.animPreviewControls}>
                <button type="button" className={styles.animControlBtn} onClick={togglePlay}>
                    {playing ? '⏸ Pause' : '▶️ Play'}
                </button>
                <button type="button" className={styles.animControlBtn} onClick={() => setMuted(m => !m)} title="Coins always play muted in the apps">
                    {muted ? '🔇 Muted' : '🔊 Sound'}
                </button>
                <button type="button" className={styles.animControlBtn} onClick={() => setLoop(l => !l)}>
                    {loop ? '🔁 Loop on' : '➡️ Loop off'}
                </button>
            </div>
        </div>
    );
}

export default function AnimatedCoinManager({ detail, onUpdated }) {
    const collectionId = detail?.id;
    const [state, setState]       = useState(null);     // { mediaType, versions, limits }
    const [mode, setMode]         = useState('STATIC'); // what the selector shows
    const [pendingFile, setPendingFile] = useState(null); // { file, url }
    const [busy, setBusy]         = useState(null);
    const [error, setError]       = useState(null);
    const [notice, setNotice]     = useState(null);
    const [dragging, setDragging] = useState(false);
    const videoInputRef  = useRef(null);
    const posterInputRef = useRef(null);
    const lastLiveId     = useRef(undefined); // undefined = nothing loaded yet

    const apply = useCallback((data) => {
        setState(data);
        const liveId = data.versions.find(v => v.isActive && v.status === 'READY')?.id || null;
        // Tell the parent (list thumbnails etc.) when the live animation changes
        // — but never for the first load. The parent shows a loading state while
        // it refetches, which remounts this component; notifying on mount would
        // make the two refetch each other forever (the panel flickers).
        if (lastLiveId.current !== undefined && liveId !== lastLiveId.current) onUpdated?.();
        lastLiveId.current = liveId;
    }, [onUpdated]);

    const load = useCallback(async () => {
        if (!collectionId) return;
        try { apply(await adminApi.getRewardMedia(collectionId)); }
        catch (err) { setError(err.message || 'Failed to load animated coin media.'); }
    }, [collectionId, apply]);

    useEffect(() => {
        lastLiveId.current = undefined;
        setState(null);
        setPendingFile(null);
        load();
    }, [load]);

    // Initialise the selector once per collection. Open the Animated panel when
    // the coin is animated or an upload is still in flight / failed, so its
    // status is never hidden behind "Static".
    const initialisedFor = useRef(null);
    useEffect(() => {
        if (!state || initialisedFor.current === collectionId) return;
        initialisedFor.current = collectionId;
        const needsAttention = state.versions.some(v => v.status !== 'READY');
        setMode(state.mediaType === 'ANIMATED' || needsAttention ? 'ANIMATED' : 'STATIC');
    }, [state, collectionId]);

    // Poll only while something is processing.
    const inFlight = !!state?.versions.some(isInFlight);
    useEffect(() => {
        if (!inFlight) return undefined;
        const timer = setInterval(load, POLL_MS);
        return () => clearInterval(timer);
    }, [inFlight, load]);

    useEffect(() => () => { if (pendingFile?.url) URL.revokeObjectURL(pendingFile.url); }, [pendingFile]);

    async function run(key, fn, successMessage) {
        setError(null);
        setNotice(null);
        setBusy(key);
        try {
            apply(await fn());
            if (successMessage) setNotice(successMessage);
        } catch (err) {
            setError(err.message || 'Something went wrong.');
        } finally {
            setBusy(null);
        }
    }

    function chooseFile(file) {
        setError(null);
        if (!file) return;
        if (!VIDEO_TYPES.includes(file.type)) {
            setError('Animation must be an .mp4 or .webm video.');
            return;
        }
        const maxMb = state?.limits?.maxUploadMb;
        if (maxMb && file.size > maxMb * 1024 * 1024) {
            setError(`Animation must be ${maxMb} MB or smaller.`);
            return;
        }
        setPendingFile({ file, url: URL.createObjectURL(file) });
    }

    async function handleModeChange(next) {
        if (next === mode) return;
        initialisedFor.current = collectionId; // an explicit choice always wins over the initial load
        if (next === 'STATIC' && state?.mediaType === 'ANIMATED') {
            if (!window.confirm('Revert this reward to a static coin? The animation is kept and can be switched back on.')) return;
            await run('type', () => adminApi.setRewardMediaType(collectionId, 'STATIC'), 'Reverted to static coin.');
            setMode('STATIC');
            onUpdated?.();
            return;
        }
        setMode(next);
        const live = state?.versions.find(v => v.isActive && v.status === 'READY');
        if (next === 'ANIMATED' && live && state.mediaType !== 'ANIMATED') {
            await run('type', () => adminApi.setRewardMediaType(collectionId, 'ANIMATED'), 'Animated coin switched back on.');
            onUpdated?.();
        }
    }

    async function handleUpload() {
        if (!pendingFile) return;
        await run('upload', () => adminApi.uploadRewardAnimation(collectionId, pendingFile.file),
            'Original stored. Generating playback, share video, GIF and poster…');
        setPendingFile(null);
    }

    const versions = state?.versions || [];
    const live     = versions.find(v => v.isActive && v.status === 'READY');
    const others   = versions.filter(v => v !== live);

    return (
        <div className={styles.animSection}>
            <div className={styles.animTypeRow} role="radiogroup" aria-label="Coin Media Type">
                <span className={styles.labelSmall}>Coin Media Type</span>
                {['STATIC', 'ANIMATED'].map(type => (
                    <label key={type} className={`${styles.animTypeOption} ${mode === type ? styles.animTypeOptionActive : ''}`}>
                        <input
                            type="radio"
                            name={`media-type-${collectionId}`}
                            value={type}
                            checked={mode === type}
                            disabled={busy === 'type'}
                            onChange={() => handleModeChange(type)}
                        />
                        {type === 'STATIC' ? 'Static' : '💎 Animated (Signature Coin)'}
                    </label>
                ))}
            </div>

            {error  && <div className={styles.errorBanner}>{error}</div>}
            {notice && <div className={styles.successBanner}>{notice}</div>}

            {mode === 'ANIMATED' && (
                <>
                    {/* ── Live animation ── */}
                    {live && (
                        <div className={styles.animCard}>
                            <div className={styles.animCardHeader}>
                                <strong>Live animation</strong>
                                <span className={styles.animStatus}>{STATUS_LABEL.READY}</span>
                                {state.mediaType !== 'ANIMATED' && <span className={styles.animStatusMuted}>(collection is currently static)</span>}
                            </div>
                            <div className={styles.animPreviewRow}>
                                <AnimationPreview src={live.animationUrl} poster={live.posterUrl} label="Looping animation" />
                                <div className={styles.animPreview}>
                                    <span className={styles.labelSmall}>Static fallback ({live.posterSource})</span>
                                    <img src={live.posterUrl} alt="Static fallback poster" className={styles.animPreviewVideo} />
                                    <div className={styles.animPreviewControls}>
                                        <button type="button" className={styles.animControlBtn} disabled={!!busy} onClick={() => posterInputRef.current?.click()}>
                                            {busy === 'poster' ? 'Uploading…' : '🖼 Replace poster'}
                                        </button>
                                        {live.posterSource === 'manual' && (
                                            <button type="button" className={styles.animControlBtn} disabled={!!busy}
                                                onClick={() => run('poster', () => adminApi.resetRewardPoster(collectionId, live.id), 'Poster re-extracted from the animation.')}>
                                                ↺ Use auto frame
                                            </button>
                                        )}
                                    </div>
                                    <input
                                        ref={posterInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        style={{ display: 'none' }}
                                        onChange={e => {
                                            const file = e.target.files?.[0];
                                            e.target.value = '';
                                            if (file) run('poster', () => adminApi.uploadRewardPoster(collectionId, live.id, file), 'Poster replaced.');
                                        }}
                                    />
                                </div>
                            </div>

                            <ul className={styles.animAssetList}>
                                <li>Original: <a href={live.originalUrl} target="_blank" rel="noreferrer">{live.originalFilename || 'original'}</a> ({live.width}×{live.height}, {(live.durationMs / 1000).toFixed(1)}s{live.hasAudio ? ', audio stripped for playback' : ''})</li>
                                <li>Share MP4: <a href={live.shareVideoUrl} target="_blank" rel="noreferrer">open</a></li>
                                <li>
                                    Share GIF:{' '}
                                    {live.gifStatus === 'READY'   && <a href={live.shareGifUrl} target="_blank" rel="noreferrer">open</a>}
                                    {live.gifStatus === 'PENDING' && <span>⏳ generating…</span>}
                                    {live.gifStatus === 'FAILED'  && (
                                        <>
                                            <span className={styles.animErrorText}>unavailable — {live.gifError} (sharing falls back to the MP4)</span>{' '}
                                            <button type="button" className={styles.animControlBtn} disabled={!!busy}
                                                onClick={() => run('gif', () => adminApi.retryRewardGif(collectionId, live.id))}>
                                                Retry GIF Generation
                                            </button>
                                        </>
                                    )}
                                </li>
                            </ul>
                            <button type="button" className={styles.animControlBtn} disabled={!!busy || inFlight}
                                onClick={() => run('regen', () => adminApi.retryRewardMedia(collectionId, live.id), 'Regenerating derivatives…')}>
                                ⚙️ Regenerate derivatives
                            </button>
                        </div>
                    )}

                    {/* ── In-flight / failed versions ── */}
                    {others.filter(v => v.status !== 'READY').map(v => (
                        <div key={v.id} className={styles.animCard} data-testid={`version-${v.status}`}>
                            <div className={styles.animCardHeader}>
                                <strong>{v.originalFilename || 'Upload'}</strong>
                                <span className={styles.animStatus}>{STATUS_LABEL[v.status]}</span>
                            </div>
                            {v.status === 'PROCESSING' && (
                                <p className={styles.coinHint}>
                                    Generating derivatives.{live ? ' The current animation stays live until this finishes.' : ''}
                                </p>
                            )}
                            {v.status === 'FAILED' && (
                                <>
                                    <p className={styles.animErrorText}>{v.error}</p>
                                    <button type="button" className={styles.animControlBtn} disabled={!!busy}
                                        onClick={() => run('retry', () => adminApi.retryRewardMedia(collectionId, v.id), 'Retrying…')}>
                                        ↻ Retry processing
                                    </button>
                                </>
                            )}
                        </div>
                    ))}

                    {/* ── Upload / replace ── */}
                    <div className={styles.animCard}>
                        <div className={styles.animCardHeader}>
                            <strong>{live ? 'Replace animation' : 'Primary animation'}</strong>
                            {!live && <span className={styles.animStatusMuted}>required</span>}
                        </div>
                        {pendingFile ? (
                            <>
                                <AnimationPreview src={pendingFile.url} label={`Preview — ${pendingFile.file.name}`} />
                                <p className={styles.coinHint}>The static fallback poster is extracted automatically after upload; you can replace it afterwards.</p>
                                <div className={styles.animPreviewControls}>
                                    <button type="button" className={styles.addItemSubmitBtn} disabled={busy === 'upload'} onClick={handleUpload}>
                                        {busy === 'upload' ? 'Uploading…' : '⬆️ Upload & process'}
                                    </button>
                                    <button type="button" className={styles.animControlBtn} disabled={busy === 'upload'} onClick={() => setPendingFile(null)}>
                                        Cancel
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div
                                className={`${styles.animDropZone} ${dragging ? styles.dropZoneDragging : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => videoInputRef.current?.click()}
                                onKeyDown={e => e.key === 'Enter' && videoInputRef.current?.click()}
                                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                                onDragLeave={() => setDragging(false)}
                                onDrop={e => { e.preventDefault(); setDragging(false); chooseFile(e.dataTransfer.files?.[0]); }}
                            >
                                <span className={styles.dropZoneIcon}>🎞️</span>
                                <span>Drop an .mp4 / .webm or click to browse</span>
                                {state?.limits && (
                                    <span className={styles.animStatusMuted}>
                                        up to {state.limits.maxUploadMb} MB · {state.limits.maxDurationSeconds}s · square works best
                                    </span>
                                )}
                            </div>
                        )}
                        <input
                            ref={videoInputRef}
                            type="file"
                            accept="video/mp4,video/webm,.mp4,.webm"
                            style={{ display: 'none' }}
                            data-testid="animation-input"
                            onChange={e => { chooseFile(e.target.files?.[0]); e.target.value = ''; }}
                        />
                    </div>
                </>
            )}
        </div>
    );
}
